import { doc, getDoc } from 'firebase/firestore';
import { Recipe } from '../types/recipe';
import { trackEvent, trackLLMGeneration } from './analyticsService';
import { getAuthHeaders } from './apiClient';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { detectLanguageFromTitle } from '../utils/detectLanguage';
import { hasRealDescription } from '../utils/recipeSource';
import { getApiBaseUrl } from '../config/apiBase';
import {
  CACHE_READ_DEADLINE_MS,
  LEGACY_TRANSLATION_BLOB_KEY,
  createPlatformObjectStore,
  discardLegacyTranslationBlob,
  isExpired,
  runWhenIdle,
  sweepTranslationCache,
  translationCacheKey,
  withDeadline,
  type ObjectStoreLike,
  type TranslationCacheEntry,
} from '../storage/TranslationCacheStore';

/**
 * How many entries the per-session memo keeps. Every recipe read or written
 * this session would otherwise stay in memory for the life of the tab; at
 * ~2–10 KB a recipe, a long browsing session adds up. Oldest-first eviction —
 * a Map iterates in insertion order — and an evicted entry is simply read from
 * disk again.
 */
export const SESSION_MEMO_LIMIT = 500;
/** The sweep runs at boot and again after this many writes, so the entry cap holds within a session too. */
export const SWEEP_EVERY_N_WRITES = 200;

/**
 * The single-blob localStorage key the device cache lived under before #695.
 * Only referenced so its removal can be asserted; nothing writes it anymore.
 */
export const TRANSLATION_CACHE_KEY = LEGACY_TRANSLATION_BLOB_KEY;

/**
 * Shape version of a stored translation. v1 covered title/ingredients/
 * instructions/dishTypes/cuisines/diets; v2 adds summary and the ingredient- and
 * instruction-section headers. Stored entries carry the version they were
 * written at so a v1 entry can be re-fetched — but only for recipes that
 * actually have those extra fields (see `needsExtendedFields`), otherwise every
 * already-complete translation would be needlessly regenerated at API cost.
 */
export const TRANSLATION_SCHEMA_VERSION = 2;

/**
 * True when a recipe carries text that only v2 of the payload covers. A recipe
 * with no real description and no section headers is fully served by a v1 entry.
 */
export function needsExtendedFields(recipe: Recipe): boolean {
  return (
    hasRealDescription(recipe) ||
    (recipe.ingredientGroups?.length ?? 0) > 0 ||
    (recipe.instructionSections?.length ?? 0) > 0
  );
}

/**
 * The language to translate FROM.
 *
 * `recipe.originalLanguage` is authoritative when set, but it is routinely
 * absent — it is not persisted on every recipe, and until recently the detail
 * read path dropped it entirely. Falling back to `'en'` in that case is what
 * made French recipes look like they were already English. A caller-supplied
 * detection is therefore preferred over the guess, and only used as the guess
 * when nothing better exists.
 */
export function resolveSourceLanguage(
  recipe: Pick<Recipe, 'originalLanguage'>,
  detectedLanguage?: 'en' | 'fr',
): 'en' | 'fr' {
  if (recipe.originalLanguage === 'fr' || recipe.originalLanguage === 'en') {
    return recipe.originalLanguage;
  }
  if (detectedLanguage) return detectedLanguage;
  return 'en';
}

/**
 * True when a stored translation predates the fields this recipe actually needs.
 *
 * Deliberately narrow: a v1 entry is perfectly good for a recipe with no real
 * description and no section headers, and rejecting those wholesale would bill a
 * fresh translation for every recipe already covered.
 */
export function isStaleSchema(
  stored: { schemaVersion?: number },
  recipe: Recipe,
): boolean {
  const version = stored.schemaVersion ?? 1;
  return version < TRANSLATION_SCHEMA_VERSION && needsExtendedFields(recipe);
}

/**
 * Whether a translated list still covers the list it was made from.
 *
 * The prompt contract is stated in `TranslationRequest`: same count, same order,
 * never merge/split/drop/reorder. A count that no longer matches means the entry
 * is not a translation of THIS recipe any more — either the model dropped the
 * list, or the recipe gained content after the entry was stored.
 *
 * `recipeTranslations/{id}` never expires and is not keyed on content, so the
 * second case is the common one: a recipe translated while it was still empty
 * caches `instructions: []`, the scrape heals it weeks later, and applying the
 * stored entry puts the recipe straight back to stepless — #678. Once the recipe
 * has content nothing re-scrapes it either, so the dead end is permanent.
 *
 * `0 === 0` passes, so a genuinely stepless recipe is unaffected.
 */
export function coversSource(
  translated: unknown[] | undefined,
  source: unknown[] | undefined,
): boolean {
  return (translated?.length ?? 0) === (source?.length ?? 0);
}

/** The same question about an already-applied translation. */
export function translationCoversRecipe(translated: Recipe, source: Recipe): boolean {
  return (
    coversSource(translated.instructions, source.instructions) &&
    coversSource(translated.extendedIngredients, source.extendedIngredients)
  );
}

/**
 * A copy of the recipe with no steps: the detail screen's nav-param first paint,
 * or a meal-plan stub built from list-index fields. It cannot say whether a
 * translation's steps are right — the recipe it stands for usually has them —
 * so it must never evict or overwrite a translation that has steps (#715).
 */
export function hasNoSteps(recipe: Recipe): boolean {
  return (recipe.instructions?.length ?? 0) === 0;
}

/**
 * The same question about a stored entry, before it is applied.
 *
 * A source with no steps accepts an entry that has some: applying it keeps the
 * source's own (empty) steps and takes the rest. Judging it stale instead is
 * what retranslated a placeholder over good rows (#715).
 */
export function storedCoversRecipe(
  stored: { ingredients?: unknown[]; instructions?: unknown[] },
  recipe: Recipe,
): boolean {
  return (
    (hasNoSteps(recipe) || coversSource(stored.instructions, recipe.instructions)) &&
    coversSource(stored.ingredients, recipe.extendedIngredients)
  );
}

/**
 * Rebuild ingredient groups against a translated recipe.
 *
 * The header comes from the model. The `items` are NOT sent for translation —
 * they are re-sliced positionally out of the already-translated ingredient
 * lines, which the prompt guarantees come back in the same count and order.
 * That keeps the payload from carrying every ingredient twice, and it keeps
 * `groupIngredientsForDisplay`'s text-matching fallback working: leaving French
 * `items` next to English ingredients would send every row to `ungrouped` and
 * collapse the sections whenever the positional branch doesn't fire.
 *
 * Falls back to the original groups whenever the counts don't line up, which is
 * exactly the case where positional assignment would be wrong anyway.
 */
export function applyGroupNames(
  groups: Recipe['ingredientGroups'],
  names: string[] | undefined,
  /** Original -> translated ingredient line, keyed by the untranslated text. */
  lineTranslations?: Map<string, string>,
): Recipe['ingredientGroups'] {
  if (!groups?.length) return groups;
  const rename = (index: number, fallback: string) =>
    names?.length === groups.length ? names[index] ?? fallback : fallback;

  return groups.map((group, index) => ({
    ...group,
    name: rename(index, group.name),
    // Looked up by text, not by position. Group items are a second copy of the
    // ingredient lines and do not always cover the list one-for-one, so any
    // count-based scheme silently leaves them untranslated.
    items: (group.items || []).map(item => lineTranslations?.get(item) ?? item),
  }));
}

/**
 * Same treatment for instruction sections: translated header, steps re-sliced
 * from the translated instruction list rather than translated a second time.
 */
export function applySectionNames(
  sections: Recipe['instructionSections'],
  names: string[] | undefined,
  /** Original -> translated step text, keyed by the untranslated text. */
  stepTranslations?: Map<string, string>,
): Recipe['instructionSections'] {
  if (!sections?.length) return sections;
  const rename = (index: number, fallback: string | undefined) =>
    names?.length === sections.length ? names[index] ?? fallback : fallback;

  const translated = sections.map((section, index) => ({
    ...section,
    name: rename(index, section.name),
    // Matched on text, not position: reconstruction lifts a header out of the
    // flat list, so the section step total is systematically short of
    // instructions.length and no positional scheme lines up.
    steps: (section.steps || []).map(s => ({
      ...s,
      step: stepTranslations?.get(s.step) ?? s.step,
    })),
  }));

  // `instructionSections` is usually DERIVED from the flat list by
  // normalize-then-reconstruct, and normalization merges fragmented steps — so
  // a section's text is often a join of several instructions and matches none
  // of them verbatim. When not one step resolves, these sections are simply out
  // of sync with the translated text; keeping them would render every visible
  // step in the original language, because the detail screen prefers sections
  // over the flat list. Drop them instead and let the screen rebuild the split
  // from the translated instructions, which is the same path taken by every
  // recipe that never carried sections.
  const hasLookup = !!stepTranslations?.size;
  const anyStepTranslated = translated.some((section, i) =>
    section.steps.some((s, j) => s.step !== sections[i].steps?.[j]?.step),
  );
  if (hasLookup && !anyStepTranslated) return undefined;

  return translated;
}

/** Build an original -> translated lookup from two positionally-aligned lists. */
function textMap<T>(source: T[] | undefined, translated: T[] | undefined, pick: (v: T) => string) {
  const map = new Map<string, string>();
  if (!source || !translated) return map;
  const n = Math.min(source.length, translated.length);
  for (let i = 0; i < n; i++) {
    const from = pick(source[i]);
    const to = pick(translated[i]);
    if (from && to) map.set(from, to);
  }
  return map;
}

interface TranslationRequest {
  title: string;
  /** Intro paragraph. Empty string when the recipe has only a title placeholder. */
  summary: string;
  ingredients: Array<{ original: string; name: string }>;
  instructions: Array<{ number: number; step: string }>;
  /**
   * Section headers, flattened to positional arrays ("Pour la vinaigrette").
   * Flat rather than nested so they inherit the same "same count, same order,
   * never merge/split/drop/reorder" contract the model already follows for
   * ingredients and instructions.
   */
  ingredientGroupNames: string[];
  instructionSectionNames: string[];
  dishTypes: string[];
  cuisines: string[];
  diets: string[];
}

interface TranslationResponse {
  title: string;
  summary: string;
  ingredients: Array<{ original: string; name: string }>;
  instructions: Array<{ number: number; step: string }>;
  ingredientGroupNames: string[];
  instructionSectionNames: string[];
  dishTypes: string[];
  cuisines: string[];
  diets: string[];
}

// Shape stored in Firestore under recipeTranslations/{recipeId}.{lang}
interface StoredTranslation extends TranslationResponse {
  translatedAt: string;
  /** Absent on entries written before the v2 payload. Treated as 1. */
  schemaVersion?: number;
}

/**
 * Whether a stored full translation's title can stand in for a title-only one.
 *
 * Not when the row is an empty shell — a title with no ingredients and no steps
 * is what a placeholder copy of the recipe produced, not a translation of it —
 * and not when the "translation" is the source title unchanged. #736: the shell
 * for "Fish Tacos" held "Fish Tacos" as the French, and this path put it on a
 * French cook's plan over the catalogue's "Tacos de poisson".
 */
export function storedTitleIsTranslation(
  stored: Pick<StoredTranslation, 'title' | 'ingredients' | 'instructions'>,
  sourceTitle: string,
): boolean {
  const isShell = !stored.ingredients?.length && !stored.instructions?.length;
  const isEcho = stored.title.trim().toLowerCase() === sourceTitle.trim().toLowerCase();
  return !isShell && !isEcho;
}

/**
 * L1 is a silent dependency. When the device store refuses a write — a full
 * IndexedDB quota, or a browser with it disabled — the app keeps working and
 * every already-translated recipe quietly falls through to a Firestore read on
 * every open. No error, no broken screen, nothing a user would report. It has
 * to be reported explicitly or it is invisible.
 */
const reportCacheFailure = (
  operation: 'load' | 'save',
  error: unknown,
  cachedRecipes?: number,
) => {
  trackEvent('translation_cache_unavailable', {
    operation,
    reason: error instanceof Error ? error.name : 'unknown',
    message: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200),
    cached_recipes: cachedRecipes,
  });
};

class RecipeTranslationService {
  /**
   * The device store: one record per recipe × language, in IndexedDB on web.
   * `session` memoises what this process has already read or written — hits
   * and misses both — so a recipe is looked up on disk at most once per boot.
   * A store that cannot be opened reports once and behaves as always-miss.
   */
  private readonly store: ObjectStoreLike = createPlatformObjectStore(error =>
    reportCacheFailure('load', error),
  );
  private session = new Map<string, TranslationCacheEntry | null>();
  /**
   * Bumped whenever the memo is reset. A read that was already in flight when
   * that happened must not write its answer into the new memo.
   */
  private generation = 0;
  private writesSinceSweep = 0;
  /** Latched so a full quota reports once, not once per recipe opened. */
  private cacheWriteFailed = false;
  private cacheReadFailed = false;
  readonly ready: Promise<void>;

  constructor() {
    this.ready = this.loadCache();
  }

  /**
   * Nothing is read at boot: entries are fetched on demand. What happens here
   * is housekeeping — the pre-#695 blob is dropped from localStorage (that is
   * the 3 MB the quota bug was about), and the TTL/cap sweep is queued for idle
   * time so it never sits on the path to first paint.
   */
  private async loadCache(): Promise<void> {
    this.generation += 1;
    this.session = new Map();
    await discardLegacyTranslationBlob();
    this.scheduleSweep();
  }

  private scheduleSweep(): void {
    this.writesSinceSweep = 0;
    runWhenIdle(() => {
      sweepTranslationCache(this.store).catch(error => {
        console.warn('Translation cache sweep failed:', error);
      });
    });
  }

  private remember(key: string, entry: TranslationCacheEntry | null): void {
    this.session.set(key, entry);
    if (this.session.size <= SESSION_MEMO_LIMIT) return;
    const oldest = this.session.keys().next().value;
    if (oldest !== undefined) this.session.delete(oldest);
  }

  private async readEntry(key: string): Promise<TranslationCacheEntry | null> {
    if (this.session.has(key)) return this.session.get(key)!;
    const generation = this.generation;
    let entry: TranslationCacheEntry | null = null;
    let failed = false;
    try {
      entry = await withDeadline(
        this.store.getItem<TranslationCacheEntry>(key),
        CACHE_READ_DEADLINE_MS,
      );
      this.cacheReadFailed = false;
    } catch (error) {
      failed = true;
      console.warn('Failed to read translation cache:', error);
      if (!this.cacheReadFailed) reportCacheFailure('load', error);
      this.cacheReadFailed = true;
    }
    // While this read was in flight a write may have landed for the same key,
    // or the memo may have been reset. Either way the memo is the newer truth.
    // A failed read is not a miss: it is not memoised, so the next call retries.
    if (failed || generation !== this.generation || this.session.has(key)) {
      return this.session.get(key) ?? null;
    }
    this.remember(key, entry);
    return entry;
  }

  private async writeEntry(key: string, entry: TranslationCacheEntry): Promise<void> {
    this.remember(key, entry);
    try {
      await this.store.setItem(key, entry);
      this.cacheWriteFailed = false;
      this.writesSinceSweep += 1;
      if (this.writesSinceSweep >= SWEEP_EVERY_N_WRITES) this.scheduleSweep();
    } catch (error) {
      console.warn('Failed to save translation cache:', error);
      if (!this.cacheWriteFailed) {
        reportCacheFailure('save', error, this.session.size);
      }
      this.cacheWriteFailed = true;
    }
  }

  private removeEntry(key: string): void {
    this.session.set(key, null);
    this.store.removeItem(key).catch(error => {
      console.warn('Failed to evict translation cache entry:', error);
    });
  }

  private async getCachedTranslation(
    recipeId: string,
    targetLanguage: 'en' | 'fr',
  ): Promise<Recipe | null> {
    const key = translationCacheKey(recipeId, targetLanguage);
    const cached = await this.readEntry(key);
    if (!cached) return null;

    if (isExpired(cached, Date.now())) {
      this.removeEntry(key);
      return null;
    }

    return cached.recipe;
  }

  /**
   * The L1 read every caller should use: a hit only counts when it still covers
   * the recipe in hand.
   *
   * L1 stores the fully-applied Recipe, so a bad L2 entry that was applied once
   * is pinned on the device for CACHE_EXPIRY_DAYS. Validating only at the
   * Firestore layer would leave the user looking at a stepless recipe for
   * another 30 days after the fix shipped. A stale entry is dropped here so the
   * next read is a genuine miss and the caller retranslates.
   */
  private async takeCachedTranslation(
    recipeId: string,
    recipe: Recipe,
    targetLanguage: 'en' | 'fr',
  ): Promise<Recipe | null> {
    const cached = await this.getCachedTranslation(recipeId, targetLanguage);
    if (!cached) return null;
    if (translationCoversRecipe(cached, recipe)) return cached;
    // Unusable for a copy without steps, but still right for the recipe (#715).
    if (hasNoSteps(recipe) && !hasNoSteps(cached)) return null;

    this.removeEntry(translationCacheKey(recipeId, targetLanguage));
    trackEvent('translation_cache_invalidated', {
      recipe_id: recipeId,
      language: targetLanguage,
      layer: 'device',
      cached_instructions: cached.instructions?.length ?? 0,
      recipe_instructions: recipe.instructions?.length ?? 0,
      cached_ingredients: cached.extendedIngredients?.length ?? 0,
      recipe_ingredients: recipe.extendedIngredients?.length ?? 0,
    });
    return null;
  }

  /**
   * L1 holds the applied Recipe under the recipe's id, so an applied copy with
   * no steps would read as stale to the full recipe on its next open and be
   * evicted — churn that also reports a false invalidation. L2 still answers it.
   */
  private cacheUnlessStepless(
    recipeId: string,
    targetLanguage: 'en' | 'fr',
    source: Recipe,
    translated: Recipe,
  ): void {
    if (hasNoSteps(source)) return;
    this.setCachedTranslation(recipeId, targetLanguage, translated);
  }

  private setCachedTranslation(recipeId: string, targetLanguage: 'en' | 'fr', recipe: Recipe): void {
    // Fire-and-forget: the caller has the translation in hand already.
    void this.writeEntry(translationCacheKey(recipeId, targetLanguage), {
      recipe,
      timestamp: Date.now(),
    });
  }

  // ── Firestore L2 cache ────────────────────────────────────────────────────

  private async getFirestoreTranslation(
    recipeId: string,
    targetLanguage: 'en' | 'fr',
  ): Promise<StoredTranslation | null> {
    if (!isFirebaseEnabled()) return null;
    try {
      const snap = await getDoc(doc(firestore!, 'recipeTranslations', recipeId));
      const data = snap.data()?.[targetLanguage] as StoredTranslation | undefined;
      return data ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Merge a stored translation onto a recipe. Shared by translateRecipe (L2 hit)
   * and translateRecipeFromStore, so both produce byte-identical output.
   */
  private applyStoredTranslation(
    recipe: Recipe,
    stored: StoredTranslation,
    fromLanguage: 'en' | 'fr',
    targetLanguage: 'en' | 'fr',
  ): Recipe {
    // A list that no longer covers the recipe is not a translation of it — see
    // coversSource. Keep the recipe's own text rather than overwriting real
    // content with a short or empty list (#678). Callers treat a mismatch as a
    // cache miss; this is the belt to that pair of braces.
    const ingredientsUsable = coversSource(stored.ingredients, recipe.extendedIngredients);
    const instructionsUsable = coversSource(stored.instructions, recipe.instructions);

    return {
      ...recipe,
      title: stored.title,
      // Only adopt a translated summary when one came back. A v1 stored entry
      // has no `summary`, and overwriting with '' would erase the description.
      summary: stored.summary || recipe.summary,
      extendedIngredients: ingredientsUsable
        ? stored.ingredients.map((ing, index) => ({
            ...(recipe.extendedIngredients || [])[index],
            original: ing.original,
            name: ing.name,
          }))
        : recipe.extendedIngredients,
      instructions: instructionsUsable
        ? stored.instructions.map((inst, index) => ({
            ...(recipe.instructions || [])[index],
            number: inst.number,
            step: inst.step,
          }))
        : recipe.instructions,
      ingredientGroups: applyGroupNames(
        recipe.ingredientGroups,
        stored.ingredientGroupNames,
        ingredientsUsable
          ? textMap(recipe.extendedIngredients, stored.ingredients, i => i.original)
          : undefined,
      ),
      instructionSections: applySectionNames(
        recipe.instructionSections,
        stored.instructionSectionNames,
        instructionsUsable
          ? textMap(recipe.instructions, stored.instructions, i => i.step)
          : undefined,
      ),
      dishTypes: stored.dishTypes,
      cuisines: stored.cuisines,
      diets: stored.diets,
      isTranslated: true,
      // Set here and by the fresh-translation merge in `translateRecipe`. It
      // tells the grocery list the ingredient text is real rather than a
      // title-only fallback, and makes generateWeekSnapshot invalidate a cached list when a full
      // translation lands on top of a title-only one (#626). False when the
      // stored ingredients were rejected above: the lines on screen are the
      // originals, and claiming otherwise would freeze a grocery list built
      // from untranslated text.
      ingredientsTranslated: ingredientsUsable,
      translatedFrom: fromLanguage,
      originalLanguage: recipe.originalLanguage,
      availableLanguages: [...(recipe.availableLanguages || []), targetLanguage],
    };
  }

  /**
   * Return a fully translated recipe (title AND ingredients AND instructions)
   * using only already-stored translations — device cache first, then Firestore.
   *
   * Never calls the translation API. This is what the meal plan and grocery list
   * use: switching language must not fire one Gemini call per planned meal.
   * Returns null when no stored translation exists, so callers can fall back.
   */
  async translateRecipeFromStore(
    recipe: Recipe,
    targetLanguage: 'en' | 'fr',
    detectedLanguage?: 'en' | 'fr',
  ): Promise<Recipe | null> {
    await this.ready;
    const recipeId = recipe.personalRecipeId || recipe.id.toString();

    const fromLanguage = resolveSourceLanguage(recipe, detectedLanguage);
    if (fromLanguage === targetLanguage) return recipe;

    const cached = await this.takeCachedTranslation(recipeId, recipe, targetLanguage);
    if (cached) return cached;

    const stored = await this.getFirestoreTranslation(recipeId, targetLanguage);
    if (!stored?.ingredients?.length) return null;
    if (isStaleSchema(stored, recipe)) return null;
    // This path must never call the API, so a stale entry cannot be repaired
    // here. Returning null leaves the caller showing the original language,
    // which reads correctly — applying the entry would show a recipe with no
    // steps and no explanation (#678).
    if (!storedCoversRecipe(stored, recipe)) {
      trackEvent('translation_cache_invalidated', {
        recipe_id: recipeId,
        language: targetLanguage,
        layer: 'firestore_readonly',
        cached_instructions: stored.instructions?.length ?? 0,
        recipe_instructions: recipe.instructions?.length ?? 0,
        cached_ingredients: stored.ingredients?.length ?? 0,
        recipe_ingredients: recipe.extendedIngredients?.length ?? 0,
      });
      return null;
    }

    const translated = this.applyStoredTranslation(
      recipe,
      stored,
      fromLanguage,
      targetLanguage,
    );
    this.cacheUnlessStepless(recipeId, targetLanguage, recipe, translated);
    return translated;
  }

  /**
   * The device-cache half of `translateRecipe`, and nothing else — same key,
   * same expiry, no language reasoning of its own so the two cannot diverge.
   *
   * Exposed so a caller can tell an instant hit from a round trip *before* it
   * announces a wait. The recipe detail screen showed a "Traduction…" label and
   * paid two extra renders on every open of an already-cached recipe, because
   * it could not ask that question without committing to the slow path.
   */
  async getCachedRecipeTranslation(
    recipe: Recipe,
    targetLanguage: 'en' | 'fr',
  ): Promise<Recipe | null> {
    await this.ready;
    return await this.takeCachedTranslation(
      recipe.personalRecipeId || recipe.id.toString(),
      recipe,
      targetLanguage,
    );
  }

  async translateRecipe(
    recipe: Recipe,
    targetLanguage: 'en' | 'fr',
    detectedLanguage?: 'en' | 'fr',
  ): Promise<Recipe> {
    await this.ready;
    const recipeId = recipe.personalRecipeId || recipe.id.toString();

    // L1: device store (IndexedDB on web), memoised per session
    const cached = await this.takeCachedTranslation(recipeId, recipe, targetLanguage);
    if (cached) {
      return cached;
    }

    // Determine source language. `detectedLanguage` wins: callers run
    // detectRecipeLanguage() first, and re-deriving from the field alone here
    // threw that answer away. On any recipe whose `originalLanguage` was missing
    // that silently produced fromLanguage === targetLanguage, so this returned
    // the recipe untranslated with no API call and no error.
    const fromLanguage = resolveSourceLanguage(recipe, detectedLanguage);
    if (fromLanguage === targetLanguage) {
      return recipe;
    }

    // L2: Firestore (shared across all users, no API cost).
    //
    // An entry that no longer covers the recipe is a MISS, not truth: falling
    // through retranslates against the content the recipe has now and the
    // `{merge:true}` write below overwrites the bad row, so each affected recipe
    // repairs itself on first view (#678).
    const stored = await this.getFirestoreTranslation(recipeId, targetLanguage);
    const storedIsStale = !!stored && !storedCoversRecipe(stored, recipe);
    if (storedIsStale) {
      trackEvent('translation_cache_invalidated', {
        recipe_id: recipeId,
        language: targetLanguage,
        layer: 'firestore',
        cached_instructions: stored!.instructions?.length ?? 0,
        recipe_instructions: recipe.instructions?.length ?? 0,
        cached_ingredients: stored!.ingredients?.length ?? 0,
        recipe_ingredients: recipe.extendedIngredients?.length ?? 0,
      });
    }
    if (stored && !storedIsStale && !isStaleSchema(stored, recipe)) {
      const translatedRecipe = this.applyStoredTranslation(
        recipe,
        stored,
        fromLanguage,
        targetLanguage,
      );
      this.cacheUnlessStepless(recipeId, targetLanguage, recipe, translatedRecipe);
      return translatedRecipe;
    }

    try {
      const translationRequest: TranslationRequest = {
        title: recipe.title,
        // Gated on hasRealDescription: imported base recipes store the title as
        // a placeholder description, and translating an echo of the title is
        // paid-for noise.
        summary: hasRealDescription(recipe) ? recipe.summary! : '',
        ingredients: (recipe.extendedIngredients || []).map(ing => ({
          original: ing.original,
          name: ing.name
        })),
        instructions: (recipe.instructions || []).map(inst => ({
          number: inst.number,
          step: inst.step
        })),
        ingredientGroupNames: (recipe.ingredientGroups || []).map(g => g.name),
        instructionSectionNames: (recipe.instructionSections || []).map(s => s.name ?? ''),
        dishTypes: recipe.dishTypes || [],
        cuisines: recipe.cuisines || [],
        diets: recipe.diets || []
      };

      console.log(`Translating recipe "${recipe.title}" from ${fromLanguage} to ${targetLanguage}`);

      const translateHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/api/ai/translate`, {
        method: 'POST',
        headers: translateHeaders,
        // `recipeId` makes the server the one that persists the result (#711):
        // `recipeTranslations/{id}` and the card's `translatedTitles`, which
        // this client is not allowed to write.
        body: JSON.stringify({
          type: 'recipe',
          recipeId,
          payload: { data: translationRequest, fromLanguage, toLanguage: targetLanguage },
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `Server returned ${response.status}`);
      }

      const data = await response.json();

      trackLLMGeneration({
        $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
        $ai_span_name: 'recipe_translation',
        $ai_model: data.model,
        $ai_provider: 'google',
        $ai_input_tokens: data.input_tokens,
        $ai_output_tokens: data.output_tokens,
        $ai_latency: data.latency_s,
      });

      // Parse the JSON response
      let translatedData: TranslationResponse;
      try {
        const jsonMatch = data.result.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          throw new Error('No JSON found in response');
        }
        translatedData = JSON.parse(jsonMatch[0]);
      } catch (parseError) {
        console.error('Failed to parse translation response:', data.result);
        throw new Error('Invalid translation response format');
      }

      // `translatedData` is an unvalidated JSON.parse of a model response. The
      // prompt asks for one line out per line in, but a response that drops the
      // instruction list would otherwise be written straight over real steps —
      // and then persisted, making the loss permanent (#678). Keep the recipe's
      // own text for any list that came back short.
      const ingredientsUsable = coversSource(
        translatedData.ingredients,
        recipe.extendedIngredients,
      );
      const instructionsUsable = coversSource(translatedData.instructions, recipe.instructions);
      if (!ingredientsUsable || !instructionsUsable) {
        trackEvent('translation_response_incomplete', {
          recipe_id: recipeId,
          language: targetLanguage,
          returned_instructions: translatedData.instructions?.length ?? 0,
          recipe_instructions: recipe.instructions?.length ?? 0,
          returned_ingredients: translatedData.ingredients?.length ?? 0,
          recipe_ingredients: recipe.extendedIngredients?.length ?? 0,
        });
      }

      // Create translated recipe
      const translatedRecipe: Recipe = {
        ...recipe,
        title: translatedData.title,
        summary: translatedData.summary || recipe.summary,
        extendedIngredients: ingredientsUsable
          ? translatedData.ingredients.map((ing, index) => ({
              ...(recipe.extendedIngredients || [])[index],
              original: ing.original,
              name: ing.name
            }))
          : recipe.extendedIngredients,
        instructions: instructionsUsable
          ? translatedData.instructions.map((inst, index) => ({
              ...(recipe.instructions || [])[index],
              number: inst.number,
              step: inst.step
            }))
          : recipe.instructions,
        ingredientGroups: applyGroupNames(
          recipe.ingredientGroups,
          translatedData.ingredientGroupNames,
          ingredientsUsable
            ? textMap(recipe.extendedIngredients, translatedData.ingredients, i => i.original)
            : undefined,
        ),
        instructionSections: applySectionNames(
          recipe.instructionSections,
          translatedData.instructionSectionNames,
          instructionsUsable
            ? textMap(recipe.instructions, translatedData.instructions, i => i.step)
            : undefined,
        ),
        dishTypes: translatedData.dishTypes,
        cuisines: translatedData.cuisines,
        diets: translatedData.diets,
        isTranslated: true,
        // The same flag `applyStoredTranslation` sets. Without it the planner's
        // background fill read every fresh translation as a failure, and the
        // copy cached below kept the grocery row a skeleton on this device.
        ingredientsTranslated: ingredientsUsable,
        translatedFrom: fromLanguage,
        originalLanguage: recipe.originalLanguage,
        availableLanguages: [...(recipe.availableLanguages || []), targetLanguage]
      };

      // The server has already persisted this to Firestore (L2) — or declined
      // to, by the same coverage rule, and never over a row that has steps from a
      // source that has none (#715) — so only the device cache (L1) is written
      // here. An incomplete response is not cached either: applied once, it would
      // pin a stepless recipe on this device for 30 days (#678).
      if (ingredientsUsable && instructionsUsable) {
        this.cacheUnlessStepless(recipeId, targetLanguage, recipe, translatedRecipe);
      }

      console.log(`Successfully translated recipe "${recipe.title}" to ${targetLanguage}`);
      return translatedRecipe;

    } catch (error) {
      console.error('Translation failed:', error);
      throw new Error(`Failed to translate recipe: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async detectRecipeLanguage(recipe: Recipe): Promise<'en' | 'fr' | 'unknown'> {
    // If already detected, return it
    if (recipe.originalLanguage && recipe.originalLanguage !== 'unknown') {
      return recipe.originalLanguage;
    }

    // Simple detection based on content
    const allText = [
      recipe.title,
      ...(recipe.extendedIngredients || []).map(ing => ing.original),
      ...(recipe.instructions || []).map(inst => inst.step)
    ].join(' ').toLowerCase();

    const frenchIndicators = [
      'cuillère', 'gramme', 'litre', 'beurre', 'farine', 'œuf', 'lait', 'avec', 'dans', 'pour'
    ];

    const englishIndicators = [
      'cup', 'tablespoon', 'teaspoon', 'butter', 'flour', 'egg', 'milk', 'with', 'and', 'the'
    ];

    let frenchScore = 0;
    let englishScore = 0;

    frenchIndicators.forEach(word => {
      if (allText.includes(word)) frenchScore++;
    });

    englishIndicators.forEach(word => {
      if (allText.includes(word)) englishScore++;
    });

    if (frenchScore > englishScore && frenchScore >= 2) {
      return 'fr';
    } else if (englishScore > frenchScore && englishScore >= 2) {
      return 'en';
    } else {
      return 'unknown';
    }
  }

  detectLanguageFromTitle(title: string): 'en' | 'fr' | 'unknown' {
    return detectLanguageFromTitle(title);
  }

  async clearCache(): Promise<void> {
    this.generation += 1;
    this.session = new Map();
    try {
      await this.store.clear();
    } catch (error) {
      console.warn('Failed to clear translation cache:', error);
    }
  }

  /**
   * Translate only the title of a recipe (lightweight, for meal plan display).
   * Uses the same cache infrastructure with a 'title_' prefix key.
   */
  async translateTitle(
    recipeId: string,
    title: string,
    originalLanguage: 'en' | 'fr' | 'unknown' | undefined,
    targetLanguage: 'en' | 'fr'
  ): Promise<string> {
    await this.ready;
    const fromLanguage = originalLanguage === 'fr' ? 'fr' : 'en';
    if (fromLanguage === targetLanguage) {
      return title;
    }

    // L1: device store
    const cacheKey = `title_${recipeId}`;
    const cached = await this.getCachedTranslation(cacheKey, targetLanguage);
    // A device that read the #736 shell cached its echo for 30 days; an echo is
    // never a translation worth serving from here.
    if (cached && cached.title.trim().toLowerCase() !== title.trim().toLowerCase()) {
      return cached.title;
    }

    // L2: Firestore — check both the full translation and the title-only key
    const stored = await this.getFirestoreTranslation(recipeId, targetLanguage);
    if (stored?.title && storedTitleIsTranslation(stored, title)) {
      this.setCachedTranslation(cacheKey, targetLanguage, { title: stored.title } as Recipe);
      return stored.title;
    }
    if (isFirebaseEnabled()) {
      try {
        const snap = await getDoc(doc(firestore!, 'recipeTranslations', recipeId));
        const titleOnly = snap.data()?.[`${targetLanguage}_title`] as string | undefined;
        if (titleOnly) {
          this.setCachedTranslation(cacheKey, targetLanguage, { title: titleOnly } as Recipe);
          return titleOnly;
        }
      } catch {
        // fall through to API call
      }
    }

    const titleHeaders = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/translate`, {
      method: 'POST',
      headers: titleHeaders,
      body: JSON.stringify({
        type: 'title',
        recipeId,
        payload: { title, fromLanguage, toLanguage: targetLanguage },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(err.error || `Server returned ${response.status}`);
    }

    const data = await response.json();
    const translatedTitle = data.result;

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'recipe_translation',
      $ai_model: data.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });

    // The server stored it under `{lang}_title` and put it on the card's
    // document (the catalogue recipe, or this cook's saved copy); see
    // api/_lib/translationStore.
    this.setCachedTranslation(cacheKey, targetLanguage, { title: translatedTitle } as Recipe);

    return translatedTitle;
  }

  isAvailable(): boolean {
    return true;
  }
}

export default new RecipeTranslationService();